import React from 'react';

import {
  Add as AddIcon,
  BorderColor as BorderColorIcon,
  Redo as RedoIcon,
} from '@mui/icons-material';
import { Button, Typography } from '@mui/material';

import { DEFAULT_VALUE_IF_NIL, STATUSES_FILTER_OPTIONS } from '@clearverify/constants';
import { formatPhoneNumber } from '@clearverify/utils';

import {
  UserStatus,
  useResendInviteToVerifierStaffMutation,
  type VerifierStaffItemFragment,
} from 'src/api';
import { ClickableLabel, UserStatusChip } from 'src/components/common';
import {
  Table,
  TableColumn,
  TableFilter,
  TableRowAction,
  TableTopAction,
} from 'src/components/table';
import { ModalId } from 'src/constants/modals';
import { BusinessRole } from 'src/constants/user';
import { useNotification } from 'src/hooks/app';
import { useFilterStorage, useScreenInfo, useSearch } from 'src/hooks/common';
import { useModal } from 'src/hooks/shared';
import { ResendInviteModalArgs } from 'src/modals/invite';
import { VerifierStaffViewDetailsByVerifierAdminModalArgs } from 'src/modals/verifierStaff';
import { PaginationContextValue } from 'src/providers/PaginationProvider';
import { hasOneOfRoles } from 'src/utils/role';
import { getUserFullName } from 'src/utils/user';

type VerifierStaffTableVerifierAdminProps = {
  verifierStaffList: VerifierStaffItemFragment[];
  verifierStaffCount: number;
  onRefresh?: () => Promise<void>;
  pagination: PaginationContextValue;
  loading: boolean;
};

export const VerifierStaffTableVerifierAdmin: React.FC<VerifierStaffTableVerifierAdminProps> = ({
  verifierStaffList,
  verifierStaffCount,
  onRefresh,
  pagination,
  loading,
}) => {
  const { isMobile } = useScreenInfo();

  const { setSearchValue } = useSearch();
  const { setSortOption } = useFilterStorage();

  const { openModal } = useModal();
  const { createApolloContext } = useNotification();

  const [resendInviteToVerifierStaff] = useResendInviteToVerifierStaffMutation({
    context: createApolloContext({
      successMessage: 'Invite has been resent successfully',
    }),
  });

  const rowActions = React.useMemo(
    (): Array<TableRowAction<VerifierStaffItemFragment>> => [
      {
        startIcon: <BorderColorIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'View Details',
        onAction: verifierStaff => {
          const isAdmin = checkIsAdmin(verifierStaff);
          const args: VerifierStaffViewDetailsByVerifierAdminModalArgs = {
            verifierStaff,
            isAdmin,
          };
          openModal(ModalId.VerifierStaffViewDetailsByVerifierAdmin, args);
        },
      },
      {
        startIcon: <RedoIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'Resend Invite',
        hidden: staff => staff.user.status !== UserStatus.Pending,
        onAction: staff => {
          const args: ResendInviteModalArgs = {
            toStaff: 'company',
            staffEmail: staff.user.email,
            onSubmit: async () => {
              const { data } = await resendInviteToVerifierStaff({
                variables: { verifierStaffId: staff.id },
              });
              return data?.resendInviteToVerifierStaff;
            },
          };

          openModal(ModalId.ResendInvite, args);
        },
      },
    ],
    [openModal, resendInviteToVerifierStaff],
  );

  const topActions = React.useMemo(
    (): Array<TableTopAction> => [
      {
        actionName: 'Add',
        onAction: () => openModal(ModalId.InviteVerifierStaffByVerifierAdmin),
        icon: <AddIcon />,
        show: true,
        render() {
          return (
            <Button
              key={this.actionName}
              variant="contained"
              startIcon={this.icon}
              onClick={this.onAction}
              size={isMobile ? 'large' : 'medium'}
            >
              New
            </Button>
          );
        },
      },
    ],
    [openModal, isMobile],
  );

  return (
    <Table
      dataSource={verifierStaffList}
      rowCount={verifierStaffCount}
      columns={columns}
      rowActions={rowActions}
      topActions={topActions}
      onSearch={setSearchValue}
      onSort={setSortOption}
      pagination={pagination}
      filters={filters}
      onRefresh={onRefresh}
      loading={loading}
    />
  );
};

const columns: Array<TableColumn<VerifierStaffItemFragment>> = [
  {
    title: 'Full Name',
    key: 'user.firstName',
    sortable: true,
    render: (_valueByDataPath, verifierStaff, { rowActions }) => {
      const isAdmin = checkIsAdmin(verifierStaff);
      return (
        <ClickableLabel
          type="button"
          onClick={() => rowActions?.[0]?.onAction(verifierStaff)}
          label={getUserFullName(verifierStaff.user)}
          labelPostfix={isAdmin ? 'Admin' : undefined}
          withoutLogo
        />
      );
    },
  },
  {
    title: 'Phone Number',
    key: 'user.phoneNumber',
    render: (_valueByDataPath, verifierStaff) => {
      return (
        <Typography variant="body2">
          {formatPhoneNumber(verifierStaff.user.phoneNumber) || DEFAULT_VALUE_IF_NIL}
        </Typography>
      );
    },
  },
  {
    title: 'Email',
    key: 'user.email',
    sortable: true,
    nonNullSortable: true,
    render: (_valueByDataPath, verifierStaff) => {
      return (
        <Typography variant="body2">{verifierStaff.user.email || DEFAULT_VALUE_IF_NIL}</Typography>
      );
    },
  },
  {
    title: 'Status',
    key: 'user.status',
    sortable: true,
    nonNullSortable: true,
    render: (_valueByDataPath, verifierStaff) => {
      return <UserStatusChip userStatus={verifierStaff.user.status} />;
    },
  },
];

const filters: TableFilter[] = [
  {
    id: 'status',
    type: 'select',
    fieldProps: {
      label: 'Status',
      multiple: true,
      options: STATUSES_FILTER_OPTIONS,
    },
  },
];

export const checkIsAdmin = (staff: VerifierStaffItemFragment) =>
  hasOneOfRoles(staff, [BusinessRole.VerifierAdmin]);
